import { ContractFileCategory } from 'compliance-api/generated';
import { useCallback, useState } from 'react';
import {
  SelectedContractFile,
  contractFileCategoryOptions,
  contractFileToInitItem,
  formatFileSize,
  uploadContractFiles,
} from '@/modules/admin-dashboard/components/contractManagement/ContractByID/contractFiles.ts';
import {
  useCompleteContractFileUploadMutation,
  useInitContractFileUploadMutation,
} from '@/modules/admin-dashboard/queries/contract-management.ts';
import { BaseModalProps, Button, Dropzone, ModalContentWrapper, Select } from 'ui';
import { TrashGrayIcon } from 'ui/icons';

interface UploadContractFilesModalProps extends BaseModalProps {
  contractId: number;
}

export const UploadContractFilesModal = ({
  contractId,
  open,
  onClose,
}: UploadContractFilesModalProps) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedContractFile[]>([]);
  const [isFilesUploading, setIsFilesUploading] = useState(false);
  const { mutateAsync: initContractFileUpload, isLoading: isInitLoading } =
    useInitContractFileUploadMutation();
  const { mutateAsync: completeContractFileUpload, isLoading: isCompleteLoading } =
    useCompleteContractFileUploadMutation();
  const isLoading = isInitLoading || isCompleteLoading || isFilesUploading;

  const handleFileAccepted = useCallback((files: File[]) => {
    setSelectedFiles(currentFiles => [...currentFiles, ...files.map(file => ({ file }))]);
  }, []);

  const handleChangeFileCategory = useCallback(
    (fileIndex: number, fileCategory: ContractFileCategory | null) => {
      setSelectedFiles(currentFiles =>
        currentFiles.map((selectedFile, index) =>
          index === fileIndex
            ? { ...selectedFile, file_category: fileCategory || undefined }
            : selectedFile,
        ),
      );
    },
    [],
  );

  const handleRemoveFile = useCallback((fileIndex: number) => {
    setSelectedFiles(currentFiles => currentFiles.filter((_, index) => index !== fileIndex));
  }, []);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    setSelectedFiles([]);
    onClose();
  }, [isLoading, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!selectedFiles.length) return;

    const response = await initContractFileUpload({
      contractId,
      data: {
        files: selectedFiles.map(contractFileToInitItem),
      },
    });

    setIsFilesUploading(true);
    try {
      const storageItemIds = await uploadContractFiles(
        response.uploads || [],
        selectedFiles.map(({ file }) => file),
      );

      if (storageItemIds.length) {
        await completeContractFileUpload({
          contractId,
          data: { storage_item_ids: storageItemIds },
        });
      }

      setSelectedFiles([]);
      onClose();
    } finally {
      setIsFilesUploading(false);
    }
  }, [completeContractFileUpload, contractId, initContractFileUpload, onClose, selectedFiles]);

  return (
    <ModalContentWrapper
      title="Upload contract files"
      open={open}
      onClose={handleClose}
      classes={{ dialogPanel: 'max-w-[560px]' }}
    >
      <div className="space-y-6">
        <Dropzone
          multiple
          maxFiles={10}
          value=""
          uploadText={
            selectedFiles.length
              ? 'Add another document'
              : 'You can just drag & drop your files and we will extract all these fields and parties for you'
          }
          acceptFormat={['doc', 'pdf']}
          loading={isLoading}
          onFileAccepted={handleFileAccepted}
          onFileClear={() => setSelectedFiles([])}
        />

        {!!selectedFiles.length && (
          <div className="space-y-3">
            <div className="text-base font-medium text-gray-900">Uploaded files</div>
            <div className="space-y-2">
              {selectedFiles.map(({ file, file_category }, index) => {
                const extension = file.name.split('.').pop()?.toUpperCase() || 'FILE';

                return (
                  <div
                    key={`${file.name}_${file.size}_${index}`}
                    className="flex items-center gap-4 rounded border border-gray-200 bg-white px-4 py-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-medium text-gray-900">
                        {file.name}
                      </div>
                      <div className="mt-1 text-sm leading-5 text-gray-500">
                        {extension} <span className="mx-1 text-gray-300">|</span>{' '}
                        {formatFileSize(file.size)}
                      </div>
                    </div>

                    <Select<ContractFileCategory | null>
                      options={contractFileCategoryOptions}
                      value={file_category || null}
                      onChange={value => handleChangeFileCategory(index, value)}
                      className="w-[106px] shrink-0"
                      withBorder
                      showBorderBottom={false}
                    />

                    <button
                      type="button"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded hover:bg-gray-100"
                      onClick={() => handleRemoveFile(index)}
                      aria-label={`Remove ${file.name}`}
                    >
                      <img src={TrashGrayIcon} alt="" className="h-5 w-5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            className="w-fit px-4"
            onClick={() => void handleSubmit()}
            loading={isLoading}
            disabled={!selectedFiles.length || isLoading}
          >
            Confirm
          </Button>
        </div>
      </div>
    </ModalContentWrapper>
  );
};
